import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Globe2 } from "lucide-react";
import { Button } from "../../components/ui/Button/Button";
import { analyticsWebsites } from "../../api/analyticsWebsites";
import { useAnalyticsScope } from "../../features/analytics/useAnalyticsScope";
import {
  analyticsKey,
  type AnalyticsScope,
} from "../../features/analytics/queryKeys";
import { useAnalyticsContext } from "../../features/analytics/useAnalyticsContext";
import { rangeParams } from "../../features/analytics/dateRange";
import { tabs } from "../../features/analytics/types";
import { errorMessage } from "../../features/analytics/format";
import DateRangeControl from "../../features/analytics/components/DateRangeControl";
import RefreshStatus from "../../features/analytics/components/RefreshStatus";
import {
  ErrorState,
  Loading,
  SourceBadge,
  SourceNotice,
} from "../../features/analytics/components/States";
import OverviewView from "../../features/analytics/views/OverviewView";
import SourceReportView from "../../features/analytics/views/SourceReportView";
import PagesView from "../../features/analytics/views/PagesView";
import ConnectionsView from "../../features/analytics/views/ConnectionsView";
import styles from "../../features/analytics/Analytics.module.css";
import { sourceSelection } from "../../features/analytics/reportSelection";
import { useAnalyticsDateRange } from "../../features/analytics/useAnalyticsDateRange";
export default function AnalyticsWorkspace() {
  const { scope, ready, error, retry, canManage } = useAnalyticsScope();
  const { websiteId = "" } = useParams();
  if (error)
    return (
      <main className={styles.root}>
        <ErrorState error={new Error(error)} retry={retry} />
      </main>
    );
  if (!ready)
    return (
      <main className={styles.root}>
        <Loading label="Loading workspace…" />
      </main>
    );
  return (
    <Workspace
      key={`${scope.userId}:${scope.organizationId}:${websiteId}`}
      scope={scope}
      id={websiteId}
      canManage={canManage}
    />
  );
}
function Workspace({
  scope,
  id,
  canManage,
}: {
  scope: AnalyticsScope;
  id: string;
  canManage: boolean;
}) {
  const { tab = "overview" } = useParams();
  const navigate = useNavigate();
  const { params, setParams, range } = useAnalyticsDateRange();
  const website = useQuery({
    queryKey: analyticsKey(scope, "website", id),
    queryFn: ({ signal }) => analyticsWebsites.get(id, signal),
    staleTime: 30000,
  });
  const choices = useQuery({
    queryKey: analyticsKey(scope, "websites", "switcher"),
    queryFn: ({ signal }) => analyticsWebsites.list({ page_size: 100 }, signal),
    staleTime: 30000,
  });
  const context = useAnalyticsContext(scope, id, range, !!website.data);
  const [exporting, setExporting] = useState(false),
    [exportError, setExportError] = useState<string | null>(null);
  if (website.isPending)
    return (
      <main className={styles.root}>
        <Loading label="Loading website…" />
      </main>
    );
  if (website.isError)
    return (
      <main className={styles.root}>
        <Link to="/analytics" className={styles.back}>
          <ArrowLeft size={16} />
          All websites
        </Link>
        <ErrorState error={website.error} retry={() => website.refetch()} />
      </main>
    );
  if (website.data.merged_into_id)
    return (
      <Navigate
        replace
        to={`/analytics/websites/${website.data.merged_into_id}/${tab}?${params}`}
      />
    );
  if (!tabs.some((t) => t.id === tab))
    return (
      <Navigate replace to={`/analytics/websites/${id}/overview?${params}`} />
    );
  const viewProps = context.data
    ? { scope, id, range, context: context.data }
    : undefined;
  const exportReport = async () => {
    setExporting(true);
    setExportError(null);
    const source =
      tab === "search" ? "gsc" : tab === "traffic" ? "ga4" : undefined;
    const selection =
      source && context.data
        ? sourceSelection(
            source,
            params,
            context.data.sources[source].available_breakdowns,
          )
        : undefined;
    const report =
      tab === "overview"
        ? "overview"
        : tab === "pages"
          ? "pages"
          : selection!.report;
    const filters = {
      q: params.get("q") ?? undefined,
      sort: params.get("sort") ?? undefined,
      source,
      metric: selection?.metric,
    };
    try {
      const blob = await analyticsWebsites.export(
        id,
        report,
        { ...range },
        filters,
      );
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = `analytics-${report}-${range.start}-${range.end}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setExportError(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };
  const homeKey = `analytics-home:${scope.userId}:${scope.organizationId ?? "personal"}`;
  let homeSearch = "";
  try {
    homeSearch = sessionStorage.getItem(homeKey) ?? "";
  } catch {
    /* Fall back to the default collection. */
  }
  return (
    <main className={styles.root}>
      <Link to={`/analytics${homeSearch}`} className={styles.back}>
        <ArrowLeft size={16} />
        All websites
      </Link>
      <header className={styles.workspaceHeader}>
        <div className={styles.identity}>
          <div className={styles.siteIcon}>
            <Globe2 size={23} />
          </div>
          <div>
            <div className={styles.eyebrow}>WEBSITE ANALYTICS</div>
            <h1>{website.data.name}</h1>
            <p>{website.data.scope_value || "Website scope not supplied"}</p>
          </div>
        </div>
        <label className={styles.switcher}>
          Switch website
          <select
            value={id}
            onChange={(e) => {
              navigate(
                `/analytics/websites/${encodeURIComponent(e.target.value)}/${tab}?${rangeParams(range)}`,
              );
            }}
          >
            {!choices.data?.items.some((w) => w.id === id) && (
              <option value={id}>{website.data.name}</option>
            )}
            {choices.data?.items.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>
      </header>
      <div className={styles.toolbar}>
        <DateRangeControl
          range={range}
          onChange={(next) => {
            const nextParams = new URLSearchParams(params);
            Object.entries(next).forEach(([key, value]) =>
              nextParams.set(key, value),
            );
            nextParams.delete("page");
            nextParams.delete("pageKey");
            setParams(nextParams);
          }}
        />
        <div className={styles.headerActions}>
          <RefreshStatus
            scope={scope}
            id={id}
            range={range}
            canRefresh={canManage}
          />
          {tab !== "connections" && (
            <Button
              variant="ghost"
              icon={<Download size={16} />}
              loading={exporting}
              disabled={!context.data}
              onClick={exportReport}
            >
              Export CSV
            </Button>
          )}
        </div>
      </div>
      {exportError && (
        <p role="alert" className={styles.actionError}>
          {exportError}{" "}
          <button className={styles.textLink} onClick={exportReport}>
            Retry export
          </button>
        </p>
      )}
      <div className={styles.statusStrip}>
        {context.data &&
          (["gsc", "ga4"] as const).map((source) => (
            <SourceBadge
              key={source}
              source={source}
              state={context.data.sources[source]}
            />
          ))}
        {context.data && (
          <span className={styles.timezone}>
            {context.data.context.timezone_reason_code
              ? "Timezone unknown · UTC display fallback"
              : `Reports: ${context.data.context.reporting_timezone}`}
          </span>
        )}
      </div>
      <nav className={styles.tabs} aria-label="Website analytics">
        {tabs.map((item) => (
          <Link
            key={item.id}
            to={`/analytics/websites/${id}/${item.id}?${rangeParams(range)}`}
            aria-current={tab === item.id ? "page" : undefined}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <div className={styles.reportContent}>
        {context.isPending ? (
          <Loading label="Loading selected period…" />
        ) : context.isError ? (
          <ErrorState error={context.error} retry={() => context.refetch()} />
        ) : (
          viewProps && (
            <>
              <div className={styles.sourceNotices}>
                {(tab === "traffic"
                  ? (["ga4"] as const)
                  : tab === "search"
                    ? (["gsc"] as const)
                    : (["gsc", "ga4"] as const)
                ).map((source) => (
                  <SourceNotice
                    key={source}
                    source={source}
                    state={context.data.sources[source]}
                  />
                ))}
              </div>
              {tab === "overview" ? (
                <OverviewView
                  key={`${id}:${context.data.context.mapping_version}:${range.start}:${range.end}:${range.compare}`}
                  {...viewProps}
                />
              ) : tab === "search" ? (
                <SourceReportView {...viewProps} source="gsc" />
              ) : tab === "traffic" ? (
                <SourceReportView {...viewProps} source="ga4" />
              ) : tab === "pages" ? (
                <PagesView {...viewProps} />
              ) : (
                <ConnectionsView
                  {...viewProps}
                  website={website.data}
                  canManage={canManage}
                />
              )}
            </>
          )
        )}
      </div>
    </main>
  );
}
